'use client';

import type { MouseEvent } from 'react';
import { usePublicUi } from '@/lib/publicUi';
import styles from './LegalFooter.module.css';

const CATALOG =
  process.env.NEXT_PUBLIC_CATALOG_HOST || 'https://catalog.containd.app';
const EDITOR = 'https://templates.containd.app/';
const GET_APP = 'https://www.containd.app';
const CORPORATE = 'https://www.nextlevelorder.com';
const LEGAL = {
  privacy: 'https://www.nextlevelorderengineering.com/privacy',
  tos: 'https://www.nextlevelorderengineering.com/ToS',
  imprint: 'https://www.nextlevelorderengineering.com/impressum',
  report: 'https://containd.space/legal/report/',
  contact: 'https://containd.space/legal/contact/',
} as const;

export function reportContentHref(opts?: {
  url?: string | null;
  slug?: string | null;
}): string {
  const loc =
    typeof document !== 'undefined'
      ? document.documentElement.lang || 'en'
      : 'en';
  const locale = ['en', 'de', 'jp'].includes(loc) ? loc : 'en';
  const base = `https://containd.space/${locale}/legal/report/`;
  if (!opts?.url && !opts?.slug) return base;
  const params = new URLSearchParams();
  if (opts.url) params.set('url', opts.url);
  if (opts.slug) params.set('slug', opts.slug);
  const q = params.toString();
  return q ? `${base}?${q}` : base;
}

type FooterLink = {
  href?: string;
  label: string;
  external?: boolean;
  id?: string;
  onClick?: (event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => boolean | void;
};

function openPrivacyChoices(event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>): boolean {
  event.preventDefault();
  const w = window as Window & {
    openContaindPrivacyChoices?: (opts?: { focus?: string }) => void;
  };
  w.openContaindPrivacyChoices?.();
  return true;
}

/**
 * Required public-site legal strip (Privacy / Privacy choices / Terms / Imprint / Report / Contact).
 * `viewer` omits Catalog / Get app (those live in the share header) and centers.
 * Share pages use a hoster watermark instead of company copyright (user owns the Space).
 */
export function LegalFooter({
  lead,
  reportHref,
  onReportClick,
  variant = 'full',
}: {
  lead?: string;
  reportHref?: string;
  /** Return true to handle in-app (signed-in report). Otherwise the legal form opens. */
  onReportClick?: (event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => boolean | void;
  variant?: 'full' | 'viewer';
}) {
  const { locale, lists, extra } = usePublicUi();
  const year = new Date().getFullYear();
  const catalogHref = `${CATALOG.replace(/\/$/, '')}/${locale}/`;
  const docs = `${CATALOG.replace(/\/$/, '')}/${locale}/developers/`;
  const report = reportHref || `https://containd.space/${locale}/legal/report/`;
  const contact = `https://containd.space/${locale}/legal/contact/`;
  const links: FooterLink[] = [
    ...(variant === 'full'
      ? [
          { href: catalogHref, label: lists.footerCatalog },
          { href: docs, label: lists.footerDocs, external: true },
          { href: EDITOR, label: lists.footerEditor, external: true },
          { href: GET_APP, label: lists.footerGetApp, external: true },
          { href: CORPORATE, label: lists.footerCompany, external: true },
        ]
      : [
          { href: docs, label: lists.footerDocs, external: true },
          { href: EDITOR, label: lists.footerEditor, external: true },
          { href: CORPORATE, label: lists.footerCompany, external: true },
        ]),
    { href: LEGAL.privacy, label: lists.footerPrivacy, external: true },
    {
      label: extra.consentPrivacyChoices,
      id: 'containd-privacy-choices',
      onClick: openPrivacyChoices,
    },
    { href: LEGAL.tos, label: lists.footerTos, external: true },
    { href: LEGAL.imprint, label: lists.footerImprint, external: true },
    { href: report, label: lists.footerReport, onClick: onReportClick },
    { href: contact, label: lists.footerContact },
  ];
  return (
    <footer
      className={`${styles.footer} ${variant === 'viewer' ? styles.viewer : ''}`}
    >
      {variant === 'viewer' ? null : (
        <p className={styles.copyright}>
          © {year} Next Level Order Engineering UG (haftungsbeschränkt)
        </p>
      )}
      {lead ? <span>{lead}</span> : null}
      <span className={`${styles.links} ${variant === 'viewer' ? styles.viewerLinks : ''}`}>
        {links.map((item, i) => (
          <span key={item.label} className={styles.linkItem}>
            {i > 0 ? (
              <span className={styles.sep} aria-hidden>
                ·
              </span>
            ) : null}
            {item.href ? (
              <a
                href={item.href}
                id={item.id}
                {...(item.external
                  ? { target: '_blank', rel: 'noopener noreferrer' }
                  : {})}
                onClick={
                  item.onClick
                    ? (e) => {
                        if (item.onClick?.(e)) e.preventDefault();
                      }
                    : undefined
                }
              >
                {item.label}
              </a>
            ) : (
              <button
                type="button"
                id={item.id}
                className={styles.choicesBtn}
                onClick={item.onClick}
              >
                {item.label}
              </button>
            )}
          </span>
        ))}
      </span>
    </footer>
  );
}

export type ViewerMenuLink = {
  href?: string;
  label: string;
  external?: boolean;
  id?: string;
  onClick?: (event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => boolean | void;
};

/** Same legal/nav links as the viewer footer — for the mobile hamburger drawer. */
export function viewerMenuLinks(opts: {
  locale: string;
  lists: ReturnType<typeof usePublicUi>['lists'];
  extra: ReturnType<typeof usePublicUi>['extra'];
  reportHref?: string;
  onReportClick?: (event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => boolean | void;
}): ViewerMenuLink[] {
  const { locale, lists, extra, reportHref, onReportClick } = opts;
  const catalogHref = `${CATALOG.replace(/\/$/, '')}/${locale}/`;
  const docs = `${CATALOG.replace(/\/$/, '')}/${locale}/developers/`;
  const report = reportHref || `https://containd.space/${locale}/legal/report/`;
  const contact = `https://containd.space/${locale}/legal/contact/`;
  return [
    { href: docs, label: lists.footerDocs, external: true },
    { href: EDITOR, label: lists.footerEditor, external: true },
    { href: CORPORATE, label: lists.footerCompany, external: true },
    { href: LEGAL.privacy, label: lists.footerPrivacy, external: true },
    {
      label: extra.consentPrivacyChoices,
      id: 'containd-privacy-choices-menu',
      onClick: openPrivacyChoices,
    },
    { href: LEGAL.tos, label: lists.footerTos, external: true },
    { href: LEGAL.imprint, label: lists.footerImprint, external: true },
    { href: report, label: lists.footerReport, onClick: onReportClick },
    { href: contact, label: lists.footerContact },
    { href: catalogHref, label: lists.footerCatalog, external: true },
    { href: GET_APP, label: lists.footerGetApp, external: true },
  ];
}

